// app/ningenie/components/PWAInstallPrompt.tsx
// baby: PWA install prompt component

'use client'

import { memo, useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { X, Download } from 'lucide-react'
import { cn } from '@/lib/utils'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const PWAInstallPromptComponent = () => {
  const pathname = usePathname()
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [showIOSInstructions, setShowIOSInstructions] = useState(false)
  const isVoiceModeWebView =
    pathname === '/ningenie/mobile-voice-viewer' ||
    (typeof window !== 'undefined' && Boolean((window as any).ReactNativeWebView))

  useEffect(() => {
    if (isVoiceModeWebView) {
      setShowPrompt(false)
      setShowIOSInstructions(false)
      setDeferredPrompt(null)
      return
    }

    // baby: check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
      return
    }

    // baby: detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
    setIsIOS(iOS)

    // baby: check if user has dismissed the prompt before (localStorage)
    const dismissed = localStorage.getItem('ningenie-pwa-dismissed')
    if (dismissed) {
      // baby: check if dismissed more than 7 days ago
      const dismissedDate = new Date(dismissed)
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)
      if (daysSinceDismissed < 7) {
        return
      }
    }

    // baby: listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setShowPrompt(true)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // baby: for iOS, show instructions after a delay if not installed
    if (iOS && !isInstalled) {
      const timer = setTimeout(() => {
        setShowIOSInstructions(true)
        setShowPrompt(true)
      }, 3000) // Show after 3 seconds
      return () => {
        clearTimeout(timer)
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [isInstalled, isVoiceModeWebView])

  const handleInstall = async () => {
    if (isIOS) {
      // baby: iOS doesn't support programmatic install
      setShowIOSInstructions(true)
      return
    }

    if (!deferredPrompt) return

    try {
      await deferredPrompt.prompt()
      const choiceResult = await deferredPrompt.userChoice

      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt')
        setIsInstalled(true)
        setShowPrompt(false)
      } else {
        console.log('User dismissed the install prompt')
        localStorage.setItem('ningenie-pwa-dismissed', new Date().toISOString())
        setShowPrompt(false)
      }

      setDeferredPrompt(null)
    } catch (error) {
      console.error('Error during install:', error)
    }
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    localStorage.setItem('ningenie-pwa-dismissed', new Date().toISOString())
    if (isIOS) {
      setShowIOSInstructions(false)
    }
  }

  if (isVoiceModeWebView || isInstalled || !showPrompt) {
    return null
  }

  // baby: iOS doesn't have beforeinstallprompt, so show instructions instead
  if (isIOS && !deferredPrompt) {
    if (!showIOSInstructions) return null
  } else if (!deferredPrompt) {
    return null
  }

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4">
      <div className="rounded-3xl border border-border/60 bg-[var(--background)] shadow-xl p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 space-y-1">
            <h3 className="text-sm font-semibold">Install NINGENIE</h3>
            {isIOS && showIOSInstructions ? (
              <div className="space-y-2 text-xs text-muted-foreground">
                <p>To install on iOS:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Tap the Share button</li>
                  <li>Scroll down and tap "Add to Home Screen"</li>
                  <li>Tap "Add" to confirm</li>
                </ol>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Add to your home screen for quick access
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex gap-2">
          {!isIOS && (
            <Button
              onClick={handleInstall}
              className="flex-1 rounded-full bg-primary text-primary-foreground text-sm font-semibold"
            >
              <Download className="h-4 w-4 mr-2" />
              Install
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleDismiss}
            className={cn(
              "rounded-full text-sm font-semibold",
              isIOS ? "flex-1" : ""
            )}
          >
            {isIOS ? "Got it" : "Later"}
          </Button>
        </div>
      </div>
    </div>
  )
}

export const PWAInstallPrompt = memo(PWAInstallPromptComponent)
PWAInstallPrompt.displayName = 'PWAInstallPrompt'

